import * as React from 'react';
import { useDispatch } from 'react-redux';
import Toolbar from './toolbar';
import CustomSidebar from './sidebar';
import AlertMessage from './AlertMessage';
import { setDrawerState } from './dashboardSlice';
import GlobalModalManager from '../globalModals/GlobalModalManager';
import MaintenanceBanner from '../common/MaintenanceBanner';
import TrialDialogManager from '../plan/upgrade/TrialDialogManager';
import EncourageUserActionBanner from '../common/EncourageUserActionBanner';
import RecoveryProgressAlert from '../settings/RecoveryProgressAlert';
import { DictationModal } from '../dictation';
import CookieConsentModal from '../../components/CookieConsentModal';
// import { cookieConsent } from '../../lib/cookieConsent';
// import { clarityManager } from '../../lib/clarityManager';

// Function to check if user is from EU
const checkIsEUUser = (): boolean => {
  // EU country codes
  const euCountries = [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
    'GB', // Including UK
  ];

  try {
    // Try to get timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Check if timezone suggests EU location
    const euTimezones = [
      'Europe/',
      'Atlantic/Reykjavik',
      'Atlantic/Canary',
      'Atlantic/Faroe',
      'Atlantic/Madeira',
      'Atlantic/Azores',
      'Africa/Ceuta',
    ];

    const isEUTimezone = euTimezones.some((tz) => timezone.startsWith(tz));

    // Also try to get language/locale hints
    const language = navigator.language || '';
    const languageCountry = language.split('-')[1]?.toUpperCase();

    // Check if language suggests EU country
    if (languageCountry && euCountries.includes(languageCountry)) {
      return true;
    }

    return isEUTimezone;
  } catch {
    // If detection fails, assume EU for safety (require explicit consent)
    return true;
  }
};

export const Dashboard = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [showConsentModal, setShowConsentModal] = React.useState(false);

  // Detect mobile viewport
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set initial drawer state before first render
  React.useLayoutEffect(() => {
    dispatch(setDrawerState(isMobile ? 'closed' : 'open'));
    setIsInitialized(true);
  }, [isMobile, dispatch]);

  // Initialize consent system and check if consent modal should be shown
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if consent has been given
      const consentRecord = localStorage.getItem('cookieConsent');

      if (!consentRecord) {
        // No consent found - check if user is from EU
        const isEU = checkIsEUUser();

        if (!isEU) {
          // Non-EU user - automatically accept consent
          localStorage.setItem(
            'cookieConsent',
            JSON.stringify({
              status: 'accepted',
              timestamp: Date.now(),
              version: '2.0',
            }),
          );

          if (window.clarity) {
            window.clarity('consentv2', {
              ad_Storage: 'granted',
              analytics_Storage: 'granted',
            });
          }
        } else if (window.clarity) {
          // EU user - apply denied to Clarity but don't save to localStorage
          window.clarity('consentv2', {
            ad_Storage: 'denied',
            analytics_Storage: 'denied',
          });
        }

        // Don't show the banner
        setShowConsentModal(false);
      } else {
        // Consent exists - apply it
        try {
          const consent = JSON.parse(consentRecord);
          if (consent.status === 'accepted' && window.clarity) {
            window.clarity('consentv2', {
              ad_Storage: 'granted',
              analytics_Storage: 'granted',
            });
          } else if (consent.status === 'declined' && window.clarity) {
            window.clarity('consentv2', {
              ad_Storage: 'denied',
              analytics_Storage: 'denied',
            });
          }
        } catch {
          // Invalid consent record - apply denied but don't save to localStorage
          if (window.clarity) {
            window.clarity('consentv2', {
              ad_Storage: 'denied',
              analytics_Storage: 'denied',
            });
          }
        }
      }
    }
  }, []);

  // Handle consent decisions
  const handleConsentAccept = () => {
    // Save consent as accepted
    localStorage.setItem(
      'cookieConsent',
      JSON.stringify({
        status: 'accepted',
        timestamp: Date.now(),
        version: '2.0',
      }),
    );

    // Load analytics scripts
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('consentv2', {
        ad_Storage: 'granted',
        analytics_Storage: 'granted',
      });
    }

    setShowConsentModal(false);
  };

  const handleConsentDecline = () => {
    // Save consent as declined
    localStorage.setItem(
      'cookieConsent',
      JSON.stringify({
        status: 'declined',
        timestamp: Date.now(),
        version: '2.0',
      }),
    );

    // Ensure no analytics scripts run
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('consentv2', {
        ad_Storage: 'denied',
        analytics_Storage: 'denied',
      });
    }

    setShowConsentModal(false);
  };

  const handleConsentClose = () => {
    // Close is treated as decline
    handleConsentDecline();
  };

  return (
    <>
      {/* Only render sidebar after initial state is set and not on server */}
      {isInitialized && typeof window !== 'undefined' ? (
        <CustomSidebar>
          <div className="flex flex-col w-full h-screen relative overflow-hidden">
            <Toolbar />
            <main className="flex flex-col flex-grow w-full relative m-0 p-0 bg-background overflow-auto min-h-0">
              <MaintenanceBanner />
              <EncourageUserActionBanner />
              <AlertMessage />
              <div className="flex-grow w-full overflow-x-hidden overflow-y-auto">
                {children}
              </div>
            </main>
          </div>
        </CustomSidebar>
      ) : (
        <div className="flex w-full min-h-screen">
          <Toolbar />
        </div>
      )}
      <RecoveryProgressAlert />
      <GlobalModalManager />
      <TrialDialogManager />
      <DictationModal />
      <CookieConsentModal
        isOpen={showConsentModal}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
        onClose={handleConsentClose}
      />
    </>
  );
};

export default Dashboard;
